import { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { Button, Container, Grid, Typography, IconButton, Box } from '@mui/material';
import Page from 'src/components/Page';
import useSettings from 'src/hooks/useSettings';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { ComplementaryInfo } from 'src/sections/crs/common';
import { PatientSummary } from 'src/sections/crs/patient';
import {
  ImmunizationAddForm,
  ReferralSummary,
  ConsentForm,
  ContactForm,
  MissingNotes,
  InsuranceInfo,
  ReferralHistory,
} from 'src/sections/crs/referral/';
import { referralSummaryData } from 'src/_mock/_crs';
import { useParams } from 'react-router';
import {
  usePatient,
  useServiceRequest,
  useImmunizations,
  useCommunications,
  useDocumentReferences,
  useValueSet,
} from 'src/@nicheaim/fhir-react';
import { ServiceRequestWrapper } from 'src/@nicheaim/fhir-base/wrappers/ServiceRequest';
import { PatientWrapper } from 'src/@nicheaim/fhir-base/wrappers/Patient';
import {
  CommunicationWrapper,
  WrappedCommunication,
} from 'src/@nicheaim/fhir-base/wrappers/Communication';
import { DocumentReferenceWrapper } from 'src/@nicheaim/fhir-base/wrappers/DocumentReference';
import { useSnackbar } from 'notistack';
import Input from '../../../../components/workflow/common/components/Input';
import SelectType from '../../../../components/workflow/common/components/Select';
import HiddenType from '../../../../components/workflow/common/components/Hidden';
import DispositionType from '../../../../components/workflow/common/components/Disposition';
import ReferralWorkflowComp from '../../../../components/workflow/Workflow';
import agent from '../../../../api/agent';
import axiosInstance from 'src/utils/axios';
import { referralService } from 'src/crs/referral/services';
import FixAddressDialog from 'src/sections/crs/referral/components/workflow-step/AddressChecklistItem/FixAddressDialog';
import AddressInCountyDialog from 'src/sections/crs/referral/components/workflow-step/AddressChecklistItem/AddressInCountyDialog';
import { PaginateQuery } from 'src/api/pagination/dtos';
import { caseService } from 'src/crs/case/service';
import { isEmpty } from 'lodash';
import useAuth from '../../../../hooks/useAuth';
import _ from 'lodash';
import {
  ReferralContactAttemptResponse,
  AssessmentsFormsResponse,
} from 'src/crs/referral/services/ReferralService';
import NoteAddForm from '../../common/NoteAddForm';
import { UI_SCHEMA } from 'src/utils/utilities';
import { fToNow, parseShortFormat } from 'src/utils/formatTime';
import ButtonType from 'src/components/workflow/common/components/Button';
import LinkType from 'src/components/workflow/common/components/Link';
import LinkModalType from 'src/components/workflow/common/components/LinkModal';
import { useDialogStore } from 'src/stores/dialog';
import {
  convertValueToValueSet,
  mapAssessmentsForms,
  mapCaseToCasesDisplay,
  mapCommunicationsToConsentFormDisplay,
  mapCommunicationsToNotesDisplay,
  mapContactAttemptsReferral,
  mapImmunizationsToDisplay,
} from '../../common/common-utils';
import {
  TABLE_HEAD_ASSESSMENTS,
  TABLE_HEAD_NOTES,
  TABLE_HEAD_CONTACT,
  TABLE_HEAD_IMMUNIZATIONS,
  STATUS_OPTION_IMMUNIZATION,
  TABLE_HEAD_CONSENT_FORM,
} from '../../common/table-head';
import { TableCustom } from '../../common/TableCustom';
import { DocumentList } from '../../patient/components/patientDocuments/DocumentList';
import Iconify from 'src/components/Iconify';
import { createProgramsByServiceRequest } from 'src/services/api/referrals';
import { getRegistries } from 'src/services/api/registry';
import { CaseIntent, CaseStatus } from 'src/crs/dto/case/case.dto';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { ValueSetWrapper } from 'src/@nicheaim/fhir-base/wrappers/ValueSet';
import StartAssessmentsButton from '../../patient/components/StartAssessmentsButton';
import {
  CRS_ACLS,
  checkAclValidation,
  getRelatedAcls,
} from 'src/utils/permissions/permission.utils';
import produce from 'immer';
import { LoadingButton } from '@mui/lab';
import { wait } from 'src/utils/timers';
import { ServiceRequest } from 'src/@nicheaim/fhir-base/mappings/ServiceRequest';
import { Communication } from 'src/@nicheaim/fhir-base/mappings/Communication';
import {
  Annotation,
  Identifier,
} from 'src/nicheaim-infrastructure/application/adapters/out/repositories/fhir/resources';
import ReferralIdentifiers from './ReferralIdentifiers';
import useHealthCareServicesByServiceRequests from 'src/hooks/useHealthCareServicesByServiceRequests';

const getIdFromReference = (reference: string): string => reference.split('/')?.[1];

const ReferralDetails = memo(() => {
  const user = useAuth();

  const { themeStretch } = useSettings();

  const { enqueueSnackbar } = useSnackbar();

  const { confirmDialog } = useDialogStore();

  const [openImmunization, setOpenImmunization] = useState(false);

  const [openNote, setOpenNote] = useState(false);

  const { id: referralId = null } = useParams();

  const [patientId, setPatientId] = useState<string | null>(null);

  const [immunizationsQuery, setImmunizationsQuery] = useState({});
  const [communicationsQuery, setCommunicationsQuery] = useState({});
  const [documentReferencesQuery, setDocumentReferencesQuery] = useState({});
  const [carePlansQuery, setCarePlansQuery] = useState({});
  const [caseData, setCaseData] = useState<any | undefined | null>(null);
  const [caseDataByPatient, setCaseDataByPatient] = useState<any | undefined | null>(null);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [fixAddressModalOpen, setFixAddressModalOpen] = useState(false);
  const [addAddressTabOpen, setAddAddressTabOpen] = useState(false);
  const [addressInCountyModalOpen, setAddressInCountyModalOpen] = useState(false);
  const [openInsuranceInfo, setOpenInsuranceInfo] = useState(false);
  const [openMissingProgressNotes, setOpenMissingProgressNotes] = useState(false);
  const [openMissingLabNotes, setOpenMissingLabNotes] = useState(false);
  const [openConsentForm, setOpenConsentForm] = useState(false);
  const [openContactClientForm, setOpenContactClientForm] = useState(false);
  const [openContactPCPForm, setOpenContactPCPForm] = useState(false);
  const [checklistData, setChecklistData] = useState<any>({});
  const [contactAttempt, setContactAttempt] = useState<ReferralContactAttemptResponse[]>([]);
  const [assessmentsForms, setAssessmentsForms] = useState<AssessmentsFormsResponse[]>([]);

  const [assessmentsLinks, setAssessmentsLinks] = useState<any[]>([]);

  const [workflowStage, setWorkflowStage] = useState(undefined);
  const [referralRecord, setReferralRecord] = useState<any>({});
  const [workflowInstance, setWorkflowInstance] = useState<any>({});
  const [allUsers, setAllUsers] = useState([]);
  const [disposition, setDisposition] = useState(false);
  const [reject, setReject] = useState(false);
  const [externalRefreshWorkFlow, setExternalRefreshWorkFlow] = useState(false);
  const [rejectCounty, setRejectCounty] = useState(false);
  const [openCollapse, setOpenCollapse] = useState(false);

  const lastContactClient =
    Array.isArray(contactAttempt) &&
    contactAttempt?.filter((e) => e.noteType === 'Contact Client').pop()
      ?.referralContactAttempContactOn;
  const countContactClient =
    Array.isArray(contactAttempt) &&
    contactAttempt?.filter((e) => e.noteType === 'Contact Client').length;
  const lastContactPCP =
    Array.isArray(contactAttempt) &&
    contactAttempt?.filter((e) => e.noteType === 'Document PCP').pop()
      ?.referralContactAttempContactOn;
  const countContactPCP =
    Array.isArray(contactAttempt) &&
    contactAttempt?.filter((e) => e.noteType === 'Document PCP').length;

  const buttonDataContactClient = `[${countContactClient}/5]
  ${
    !isEmpty(lastContactClient)
      ? `(Last on ${parseShortFormat(lastContactClient || '')} - 
  ${fToNow(lastContactClient || '')})`
      : ''
  }`;

  const buttonDataContactPCP = `[${countContactPCP}/5]
  ${
    !isEmpty(lastContactPCP)
      ? `(Last on ${parseShortFormat(lastContactPCP || '')} - 
  ${fToNow(lastContactPCP || '')})`
      : ''
  }`;

  const fetchAssessments = async (surveyId: any) => {
    const result: any = await referralService.referralApiClient.assessments(
      surveyId,
      'ServiceRequest'
    );
    setAssessmentsForms(mapAssessmentsForms(result));
  };

  const fetchAssessmentsLinks = async () => {
    const result: any = await getRegistries(undefined, 'crs-patient-assessments');
    const data = result?.data;
    const JsonObject = JSON.parse(data[0]?.keyValue);
    setAssessmentsLinks(JsonObject);
  };

  const fetchReferralRecord = async (id: any) => {
    const result: any = await referralService.referralApiClient.getOne(id);

    fetchAssessmentsLinks();
    setReferralRecord(result);
  };

  const fetchContactAttempt = async (fhirId: string) => {
    const result: any = await referralService.referralApiClient.getAllContactAttempt(fhirId);
    setContactAttempt(result);
  };
  const getAllUsers = async () => {
    const result = await agent.User.getAllUsers();
    setAllUsers(result);
  };
  /*
   ** Mock Data Workflow
   */
  const REFERENCE_NAME = 'REFERRAL';
  const INITIAL_WORKFLOW = 'REFERRALS_WORKFLOW';

  useEffect(() => {
    fetchAssessments(referralId);
    fetchReferralRecord(referralId);
    fetchStageFromWorkflow();
    getAllUsers();
    fetchContactAttempt(referralId || '');
  }, [referralId]);

  const InsuranceInfoItemHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    const payload = {
      item: checklistItem,
      record: {
        ...referralSummaryData?.[0],
        fhirId: 'acafa78e-e927-4988-aff5-cddd3b402e45',
      },
    };

    try {
      const result = await agent.Workflow.fhirAttribute(payload);
      refreshChecklistHandler();
      console.log('result validate insurance info', result);
    } catch (err) {
      console.log('error on trying to InsuranceInfoItemHandler');
    }
  };
  const validateHomeItemHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setFixAddressModalOpen(true);
    setChecklistData(checklistItem);
  };
  const validateAddressInCountyItemHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setAddressInCountyModalOpen(true);
    setChecklistData(checklistItem);
  };
  const consentFormHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setOpenConsentForm(true);
    setChecklistData(checklistItem);
  };
  const missingInsuranceHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setOpenInsuranceInfo(true);
    setChecklistData(checklistItem);
  };
  const missingProgressNotesHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setOpenMissingProgressNotes(true);
    setChecklistData(checklistItem);
  };
  const missingLabNotesHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setOpenMissingLabNotes(true);
    setChecklistData(checklistItem);
  };
  const contactClientHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setOpenContactClientForm(true);
    setChecklistData(checklistItem);
  };
  const contactPCPHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setOpenContactPCPForm(true);
    setChecklistData(checklistItem);
  };
  const assignReferralItemHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    const payload = {
      item: checklistItem,
      record: {
        ...referralRecord,
        ...workflowInstance,
        assigned: {
          selectedUser: more.selectedUser,
        },
      },
    };

    try {
      await agent.Workflow.assignOwner(payload);
      refreshChecklistHandler();
    } catch (err) {
      console.log('error on trying to assignReferralItemHandler');
    }
  };
  const fetchStageFromWorkflow = async () => {
    try {
      const result = await agent.Workflow.getInstance(referralId, REFERENCE_NAME);
      setWorkflowInstance(result);
      setWorkflowStage(result?.stage?.id);
    } catch (error) {
      console.log('error', error);
    }
  };
  const handlerDisposition = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setDisposition(true);
  };
  const handlerReject = async (open: any) => {
    setReject(open);
  };

  const referralWorkflowData = useMemo(() => {
    return {
      id: 1,
      recordId: referralId,
      recordReference: REFERENCE_NAME,
      stage: {
        id: workflowStage,
      },
      referralRecord,
      workflowInstance,
    };
  }, [workflowStage, workflowInstance]);
  const WorkflowHandler = {
    ASSIGN_REFERRAL_ITEM: {
      type: 'payload',
      handler: assignReferralItemHandler,
      data: allUsers,
    },
    VALIDATE_HOME_ADDRESS_ITEM: {
      type: 'payload',
      handler: validateHomeItemHandler,
      data: true,
    },
    VALIDATE_ADDRESS_IN_COUNTY_ITEM: {
      type: 'payload',
      handler: validateAddressInCountyItemHandler,
      data: true,
    },
    MISSING_INSURANCE_INFO_ITEM: {
      type: 'payload',
      handler: missingInsuranceHandler,
      data: true,
    },
    MISSING_PROGRESS_NOTES_ITEM: {
      type: 'payload',
      handler: missingProgressNotesHandler,
      data: true,
    },
    MISSING_LAB_NOTES_ITEM: {
      type: 'payload',
      handler: missingLabNotesHandler,
      data: true,
    },
    CONSENT_FORM_ITEM: {
      type: 'payload',
      handler: consentFormHandler,
      data: true,
    },
    CONTACT_CLIENT_ITEM: {
      type: 'payload',
      handler: contactClientHandler,
      data: buttonDataContactClient,
    },
    CONTACT_PCP_ITEM: {
      type: 'payload',
      handler: contactPCPHandler,
      data: buttonDataContactPCP,
    },
    setDisposition: {
      type: 'confirm',
      handler: handlerDisposition,
      data: caseDataByPatient,
    },
    setReject: {
      type: 'confirm',
      handler: (open: boolean = true) => handlerReject(open),
      data: rejectCounty,
    },
  };
  const WorkflowData = {
    disposition,
    reject,
  };
  const WorkflowComponentMapping: any = {
    Input,
    Select: SelectType,
    Hidden: HiddenType,
    Disposition: DispositionType,
    Button: ButtonType,
    Link: LinkType,
    LinkModal: LinkModalType,
  };

  const getParseColumn = (obj: any, field: any) => {
    return (
      (obj &&
        Object.keys(obj).length > 0 &&
        obj[field] &&
        typeof obj[field] === 'string' &&
        JSON.parse(obj[field])) ||
      {}
    );
  };

  const [
    serviceRequest,
    {
      isError: isErrorServiceRequest,
      isLoading: isLoadingServiceRequest,
      isSuccess: isSuccessServiceRequest,
      refresh: refreshServiceRequest,
      update: updateServiceRequest,
    },
  ] = useServiceRequest(referralId, { map: ServiceRequestWrapper });
  const [
    patient,
    {
      isError: isErrorPatient,
      isLoading: isLoadingPatient,
      isSuccess: isSuccessPatient,
      update: updatePatient,
      refresh: refreshPatient,
    },
  ] = usePatient(patientId, {
    map: PatientWrapper,
  });

  const fhirPatientId = useMemo(
    () => referralRecord?.indexedFhirSubjectRefUri?.trim().slice('-36'),
    [referralRecord]
  );

  const [
    immunizations,
    {
      isLoading: isLoadingImmunizations,
      refresh: refreshImmunizations,
      create: createImmunization,
    },
  ] = useImmunizations({ filter: immunizationsQuery });

  const [communications, { refresh: refreshCommunications, create: createCommunication }] =
    useCommunications({
      filter: communicationsQuery,
      map: CommunicationWrapper,
    });

  const [
    documentReferences,
    {
      create: createDocumentReference,
      refresh: refreshDocumentReference,
      isFetching: isDocumentsLoading,
    },
  ] = useDocumentReferences({
    filter: documentReferencesQuery,
    map: DocumentReferenceWrapper,
  });

  const [carePlanCategory] = useValueSet('crs-service-request-category', { map: ValueSetWrapper });

  const { healthCareServices } = useHealthCareServicesByServiceRequests(referralId ? [referralId] : []);

  useEffect(() => {
    if (serviceRequest && isSuccessServiceRequest) {
      if (serviceRequest?.subject?.reference) {
        setPatientId(getIdFromReference(serviceRequest?.subject?.reference));
      }
      setCommunicationsQuery({ ...communicationsQuery, 'part-of': serviceRequest.id });
      setDocumentReferencesQuery({ ...documentReferencesQuery, related: serviceRequest.id });

      if (serviceRequest.hasCases()) {
        const careplanIds = serviceRequest.getCasesIds().join(',');
        setCarePlansQuery({ ...carePlansQuery, _id: careplanIds });
      }
    }
  }, [serviceRequest, isSuccessServiceRequest]);

  useEffect(() => {
    if (patientId) {
      setImmunizationsQuery({ ...immunizationsQuery, patient: patientId });
      getCaseListByPatient();
    }
  }, [patientId]);

  const handleCreateImmunization = async (data: any) => {
    const result = await createImmunization(data);
    refreshImmunizations();
    enqueueSnackbar('Immunization created successfully!');
    handleCloseAddImmunization();
  };

  const handleMarkItemCompleted = async () => {
    let result: any = {};
    const payload = {
      item: checklistData,
      record: {
        ...referralRecord,
        ...workflowInstance,
      },
    };

    try {
      await agent.Checklist.markItemCompleted(payload);
    } catch (err) {
      console.log('error on handleInsuranceInfo markItemCompleted', err);
    } finally {
      setChecklistData({});
      setExternalRefreshWorkFlow(true);
    }

    setExternalRefreshWorkFlow(false);

    return result;
  };

  const handleCreateCommunication = async (data: any) => {
    let result: any = {};
    const payload = {
      item: checklistData,
      record: {
        ...referralRecord,
        ...workflowInstance,
      },
    };

    try {
      await agent.Checklist.markItemCompleted(payload);
      result = await handleCreateCommunicationFhir(data);
    } catch (err) {
      console.log('error on handleCreateCommunication', err);
    } finally {
      setOpenConsentForm(false);
      setChecklistData({});
      setExternalRefreshWorkFlow(true);
    }

    setExternalRefreshWorkFlow(false);

    return result;
  };

  const handleCreateCommunicationFhir = async (data: any) => {
    let result: any = {};

    try {
      result = await createCommunication(data);

      const setNote = result?.map((x1: Communication) => ({
        id: `${x1.resourceType}/${x1.id}`,
        text: x1?.payload?.[0]?.contentString || x1?.medium?.[0]?.coding?.[0]?.display,
      }));
      const note = serviceRequest?.note ?? [];
      const setNoteServiceRequest: Annotation[] = [...setNote, ...note];

      if (setNoteServiceRequest && serviceRequest) {
        serviceRequest.note = setNoteServiceRequest;
        handleUpdateReferral(serviceRequest);
      }

      handleCloseAddNote();
      refreshCommunications();
    } catch (err) {
      console.log('error on handleCreateCommunicationFhir', err);
    }

    return result;
  };

  const handleCreateContactForm = async (data: any) => {
    let resultContactAttempt: any = {};

    const workflowInstanceData =
      workflowInstance && workflowInstance.data && typeof workflowInstance.data === 'string'
        ? JSON.parse(workflowInstance.data)
        : workflowInstance && !workflowInstance?.data
        ? {}
        : workflowInstance?.data;

    const workflowInstanceDataSchema =
      workflowInstance &&
      workflowInstance.dataSchema &&
      typeof workflowInstance.dataSchema === 'string'
        ? JSON.parse(workflowInstance.dataSchema)
        : workflowInstance && !workflowInstance?.dataSchema
        ? {}
        : workflowInstance?.dataSchema;

    const payloadData =
      workflowInstanceData && Object.keys(workflowInstanceData).length > 0
        ? _.merge(workflowInstanceData, {
            scope: {
              checklistItem: {
                ...workflowInstanceData.checklistItem,
                [data?.type === 'Contact client item' ? 'CONTACT_CLIENT_ITEM' : 'CONTACT_PCP_ITEM']:
                  {
                    validatedOn: new Date().toISOString(),
                    validatedBy: userEmail || '',
                    modifiedFields: {
                      ...data,
                    },
                  },
              },
            },
          })
        : {
            scope: {
              checklistItem: {
                [data?.type === 'Contact client item' ? 'CONTACT_CLIENT_ITEM' : 'CONTACT_PCP_ITEM']:
                  {
                    validatedOn: new Date().toISOString(),
                    validatedBy: userEmail || '',
                    modifiedFields: {
                      ...data,
                    },
                  },
              },
            },
          };

    const CLIENT_TYPE =
      data?.type === 'Contact client item'
        ? UI_SCHEMA.CLIENT_INTERVIEW_CLIENT
        : UI_SCHEMA.CLIENT_INTERVIEW_PCP;

    const payloadDataSchema =
      workflowInstanceDataSchema && Object.keys(workflowInstanceDataSchema).length > 0
        ? _.merge(workflowInstanceDataSchema, {
            type: 'object',
            properties: {
              ...CLIENT_TYPE,
            },
          })
        : {
            type: 'object',
            properties: {
              ...CLIENT_TYPE,
            },
          };

    const payloadForUpdateWorkflowinstance = {
      referenceId: referralId,
      referenceName: REFERENCE_NAME,
      data: JSON.stringify(payloadData),
      dataSchema: JSON.stringify(payloadDataSchema),
    };

    const payloadForSetItemCompleted = {
      item: checklistData,
      record: {
        ...referralRecord,
        ...workflowInstance,
      },
    };

    try {
      resultContactAttempt = await referralService.createContactAttempt(data);
      if (!isEmpty(resultContactAttempt)) {
        const resultMark = await agent.Checklist.markItemCompleted(payloadForSetItemCompleted);
        const updateWorkflowInstance = await agent.Workflow.updateWorkflowInstance(
          payloadForUpdateWorkflowinstance
        );
        fetchContactAttempt(referralId || '');
      }
    } catch (err) {
      console.log('error on handleCreateContactForm markItemCompleted', err);
    } finally {
      setChecklistData({});
      setExternalRefreshWorkFlow(true);
      console.log('externalRefreshWorkFlow in contact form', externalRefreshWorkFlow);
    }

    setExternalRefreshWorkFlow(false);

    return resultContactAttempt;
  };

  const handleSyncReferral = async () => {
    setSyncing(true);
    try {
      await wait(1000);
      const { data } = await axiosInstance.post(`/crs/referral/${serviceRequest?.id}/sync`);
    } catch (error) {
      enqueueSnackbar('There was an error trying to sync referral. Please try again', {
        variant: 'error',
      });
      setSyncing(false);
      return;
    }
    enqueueSnackbar('Referral Synced Succesfully', {
      variant: 'success',
    });
    refreshServiceRequest();
    setSyncing(false);
  };

  const handleFixAddress = async (data: any) => {
    const address = patient?.address !== undefined ? [...patient?.address, data] : [data];

    try {
      await updatePatient(
        produce(patient!, (draft) => {
          draft.address = address;
        })
      );
      await refreshPatient();
      setAddAddressTabOpen(false);
    } catch (err) {
      console.log('error on handleFixAddress', err);
    }
  };

  const saveSelectedAddress = async (address: any) => {
    const workflowInstanceData =
      workflowInstance && workflowInstance.data && typeof workflowInstance.data === 'string'
        ? JSON.parse(workflowInstance.data)
        : workflowInstance && !workflowInstance?.data
        ? {}
        : workflowInstance?.data;

    const workflowInstanceDataSchema =
      workflowInstance &&
      workflowInstance.dataSchema &&
      typeof workflowInstance.dataSchema === 'string'
        ? JSON.parse(workflowInstance.dataSchema)
        : workflowInstance && !workflowInstance?.dataSchema
        ? {}
        : workflowInstance?.dataSchema;

    const payloadData =
      workflowInstanceData && Object.keys(workflowInstanceData).length > 0
        ? _.merge(workflowInstanceData, {
            scope: {
              checklistItem: {
                ...workflowInstanceData.checklistItem,
                VALIDATE_HOME_ADDRESS_ITEM: {
                  validatedOn: new Date().toISOString(),
                  validatedBy: userEmail || '',
                  modifiedFields: {
                    ...address,
                  },
                },
              },
            },
          })
        : {
            scope: {
              checklistItem: {
                VALIDATE_HOME_ADDRESS_ITEM: {
                  validatedOn: new Date().toISOString(),
                  validatedBy: userEmail || '',
                  modifiedFields: {
                    ...address,
                  },
                },
              },
            },
          };

    const VALIDATE_HOME_ADDRESS_ITEM = UI_SCHEMA.VALIDATE_HOME_ADDRESS_ITEM;

    const payloadDataSchema =
      workflowInstanceDataSchema && Object.keys(workflowInstanceDataSchema).length > 0
        ? _.merge(workflowInstanceDataSchema, {
            type: 'object',
            properties: {
              VALIDATE_HOME_ADDRESS_ITEM,
            },
          })
        : {
            type: 'object',
            properties: {
              VALIDATE_HOME_ADDRESS_ITEM,
            },
          };

    const payloadForUpdateWorkflowinstance = {
      referenceId: referralId,
      referenceName: REFERENCE_NAME,
      data: JSON.stringify(payloadData),
      dataSchema: JSON.stringify(payloadDataSchema),
    };

    const payloadForSetItemCompleted = {
      item: checklistData,
      record: {
        ...referralRecord,
        ...workflowInstance,
        isValidRequired: false,
      },
    };

    try {
      await agent.Workflow.updateWorkflowInstance(payloadForUpdateWorkflowinstance);
      await agent.Checklist.markItemCompleted(payloadForSetItemCompleted);
    } catch (err) {
      console.log('error on saveSelectedAddress', err);
    } finally {
      setExternalRefreshWorkFlow(true);
      fetchReferralRecord(referralId);
      setFixAddressModalOpen(false);
    }

    setExternalRefreshWorkFlow(false);
  };

  const saveCountyAddress = async (updatedIsInCountyPayload: any) => {
    const workflowInstanceData =
      workflowInstance && workflowInstance.data && typeof workflowInstance.data === 'string'
        ? JSON.parse(workflowInstance.data)
        : workflowInstance.data;

    const workflowInstanceDataSchema =
      workflowInstance &&
      workflowInstance.dataSchema &&
      typeof workflowInstance.dataSchema === 'string'
        ? JSON.parse(workflowInstance.dataSchema)
        : workflowInstance && !workflowInstance?.dataSchema
        ? {}
        : workflowInstance?.dataSchema;

    const payloadData =
      workflowInstanceData && Object.keys(workflowInstanceData).length > 0
        ? _.merge(workflowInstanceData, {
            scope: {
              checklistItem: {
                ...workflowInstanceData.checklistItem,
                VALIDATE_ADDRESS_IN_COUNTY_ITEM: {
                  validatedOn: new Date().toISOString(),
                  validatedBy: userEmail || '',
                  modifiedFields: {
                    isInCounty: updatedIsInCountyPayload?.county === 'in' ? true : false,
                    Notes: updatedIsInCountyPayload?.notes,
                  },
                },
              },
            },
          })
        : {
            scope: {
              checklistItem: {
                VALIDATE_ADDRESS_IN_COUNTY_ITEM: {
                  validatedOn: new Date().toISOString(),
                  validatedBy: userEmail || '',
                  modifiedFields: {
                    isInCounty: updatedIsInCountyPayload?.county === 'in' ? true : false,
                    Notes: updatedIsInCountyPayload?.notes,
                  },
                },
              },
            },
          };

    const VALIDATE_ADDRESS_IN_COUNTY_ITEM = UI_SCHEMA.VALIDATE_ADDRESS_IN_COUNTY_ITEM;

    const payloadDataSchema =
      workflowInstanceDataSchema && Object.keys(workflowInstanceDataSchema).length > 0
        ? _.merge(workflowInstanceDataSchema, {
            type: 'object',
            properties: {
              VALIDATE_ADDRESS_IN_COUNTY_ITEM,
            },
          })
        : {
            type: 'object',
            properties: {
              VALIDATE_ADDRESS_IN_COUNTY_ITEM,
            },
          };

    const payloadForUpdateWorkflowinstance = {
      referenceId: referralId,
      referenceName: REFERENCE_NAME,
      data: JSON.stringify(payloadData),
      dataSchema: JSON.stringify(payloadDataSchema),
    };

    const payloadForSetItemCompleted = {
      item: checklistData,
      record: {
        ...referralRecord,
        ...workflowInstance,
        isValidRequired: true,
      },
    };

    let resultMark: any;
    let updateWorkflowInstance: any;
    try {
      resultMark = await agent.Checklist.markItemCompleted(payloadForSetItemCompleted);
      updateWorkflowInstance = await agent.Workflow.updateWorkflowInstance(
        payloadForUpdateWorkflowinstance
      );
    } catch (err) {
      console.log('error on saveSelectedAddress', err);
    } finally {
      setExternalRefreshWorkFlow(true);
      fetchReferralRecord(referralId);
      setAddressInCountyModalOpen(false);
    }

    setExternalRefreshWorkFlow(false);

    if (resultMark && updateWorkflowInstance) {
      handleSetRejectCondition(updatedIsInCountyPayload?.county);
    }
  };

  const handleSetRejectCondition = async (data: any) => {
    if (data === 'out' || isEmpty(data)) {
      const confirms = await confirmDialog({
        title: 'Warning',
        description: (
          <>
            <Typography gutterBottom>¡The address is out the county!</Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Do you want to reject this referral?
            </Typography>
          </>
        ),
      });

      if (confirms) {
        setRejectCounty(true);
        setReject(true);
      }
    }
  };

  const handleConfirmAddressInCounty = async (data: any) => {
    patient!.address! = data;
    await updatePatient(patient!);
    await refreshPatient();
  };

  const handleReAssingOwned = async (ownedSelected: string) => {
    try {
      const payload = {
        record: {
          ...workflowInstance,
          assigned: {
            selectedUser: ownedSelected,
          },
        },
      };

      await agent.Workflow.reAssignOwner(payload);
      fetchStageFromWorkflow();
    } catch (err) {
      console.log('error on handleReAssingOwned');
    }
  };

  const getPaginateQuery = () => {
    //@ts-ignore
    const { _id } = carePlansQuery;
    const paginationQuery: PaginateQuery = {};
    paginationQuery.filter = { fhirId: _id };
    return paginationQuery;
  };

  const getCaseList = async () => {
    const cases = await caseService.getAll(getPaginateQuery());
    if (cases) {
      setCaseData(cases);
    }
  };

  const getPaginateQueryByPatient = () => {
    const paginationQuery: PaginateQuery = {};
    paginationQuery.filter = {
      indexedSubjectFhirRefUri: `${process.env.REACT_APP_FHIR_API_BASE_URL}/Patient/${patientId}`,
      indexedStatus: 'active',
    };
    return paginationQuery;
  };

  const getCaseListByPatient = async () => {
    const cases = await caseService.getAll(getPaginateQueryByPatient());
    if (patientId && cases) {
      setCaseDataByPatient(cases);
    }
  };

  useEffect(() => {
    if (!isEmpty(carePlansQuery)) {
      getCaseList();
    }
  }, [carePlansQuery]);

  const dataCase = caseData?.data ? caseData.data.map((r: any) => mapCaseToCasesDisplay(r)) : [];

  const handleCreateCase = async (data: any) => {
    let carePlanFhirId: string = '';
    try {
      if (data?.fhirId) {
        caseService.update(data?.fhirId, {
          supportingInfo: [{ reference: `ServiceRequest/${serviceRequest?.id}` }],
        });
        carePlanFhirId = data?.fhirId;
      } else if (data === 'new') {
        const categoryValueSet = convertValueToValueSet('mint_referral', carePlanCategory);
        const payload = {
          patientId: getFhirIdFromEntity(referralRecord?.indexedFhirSubjectRefUri) || '',
          intent: 'plan' as CaseIntent,
          status: 'active' as CaseStatus,
          assignedTo: data?.referralRecord?.workflowOwnedBy,
          supportingInfo: [{ reference: `ServiceRequest/${serviceRequest?.id}` }],
          scope: [
            {
              ...(categoryValueSet && { coding: [categoryValueSet] }),
              text: categoryValueSet?.display,
            },
          ],
        };
        const result = await caseService.create(payload);
        carePlanFhirId = result?.fhirId ?? '';
      }

      if (!carePlanFhirId) return;

      const result = referralService.update(serviceRequest?.id!, {
        supportingInfo: [{ reference: `CarePlan/${carePlanFhirId}` }],
      });
    } catch (error) {
      enqueueSnackbar('An error has occurred.', { variant: 'error' });
    }
  };

  const handleCloseInsuranceInfo = () => {
    setOpenInsuranceInfo(false);
  };

  const handleCloseMissingProgressNotes = () => {
    setOpenMissingProgressNotes(false);
  };

  const handleCloseMissingLabNotes = () => {
    setOpenMissingLabNotes(false);
  };

  const handleCloseConsentForm = () => {
    setOpenConsentForm(false);
  };

  const handleCloseContactClientForm = () => {
    setOpenContactClientForm(false);
  };

  const handleCloseContactPCPForm = () => {
    setOpenContactPCPForm(false);
  };

  const handleOpenAddImmnization = () => {
    setOpenImmunization((prev) => !prev);
  };

  const handleCloseAddImmunization = () => {
    setOpenImmunization(false);
  };

  const handleOpenNote = () => {
    setOpenNote((prev) => !prev);
  };

  const handleCloseAddNote = () => {
    setOpenNote(false);
  };

  const handleWorkflowDisposition = (data: any) => {
    if (!serviceRequest?.id) return;
    handleCreateCase(data);
    createProgramsByServiceRequest(serviceRequest.id);
  };

  const userEmail = user?.getCurrentUser?.()?.email;

  const handleOpenFromIsCounty = () => setFixAddressModalOpen(true);

  const handleUpdateReferral = async (data: ServiceRequest) => {
    const id = data?.id;
    const isEditing = Boolean(id);

    let result: any = {};

    if (isEditing) {
      result = await updateServiceRequest(
        produce(serviceRequest!, (draft) => {
          draft.supportingInfo = data.supportingInfo;
          draft.note = data.note;
        })
      );
    }

    refreshServiceRequest();

    return result;
  };

  const onUpdateIdentifiers = useCallback(
    async (newIdentifiers: Identifier[]) => {
      await updateServiceRequest(
        produce(serviceRequest!, (draft) => {
          draft.identifier = newIdentifiers;
        })
      );
    },
    [serviceRequest]
  );

  return (
    <Page title="Referral Details">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          title=""
          heading=""
          links={[
            { name: 'Home', href: PATH_DASHBOARD.root },
            { name: 'CRS' },
            { name: 'Referral List', href: PATH_DASHBOARD.crs.referral },
            { name: 'Referral Details' },
          ]}
        />
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="h4">Referral Details</Typography>
          </Grid>

          <Grid item xs={6}>
            <LoadingButton
              sx={{ float: 'right' }}
              variant="contained"
              disabled={syncing || !serviceRequest?.id}
              onClick={handleSyncReferral}
              loading={syncing || !serviceRequest?.id}
            >
              Sync referral
            </LoadingButton>
          </Grid>

          <Grid item xs={12} sx={{ mt: 2 }}>
            <ReferralWorkflowComp
              data={referralWorkflowData}
              refreshRecord={fetchStageFromWorkflow}
              initialWorkflow={INITIAL_WORKFLOW}
              workflowHandler={WorkflowHandler}
              workflowData={WorkflowData}
              componentMapping={WorkflowComponentMapping}
              refreshWorkFlowExternal={externalRefreshWorkFlow}
              onWorkflowDisposition={handleWorkflowDisposition}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
              <Typography sx={{ mt: 1.5 }} variant="overline">
                collapse all
              </Typography>
              <IconButton onClick={() => setOpenCollapse(!openCollapse)}>
                <Iconify
                  icon={openCollapse ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
                />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <ReferralSummary
              referralRecord={referralRecord}
              serviceRequest={serviceRequest}
              title="Referral Summary"
              workflowInstance={workflowInstance}
              users={allUsers}
              onOwnedAssign={handleReAssingOwned}
              fhirPatient={fhirPatientId}
              handlers={{
                insuranceHandler: setOpenInsuranceInfo,
                progressNotesHandler: setOpenMissingProgressNotes,
                labNotesHandler: setOpenMissingLabNotes,
                consentHandler: setOpenConsentForm,
              }}
              referralWorkflowData={referralWorkflowData}
              openCollapseExternal={openCollapse}
              children={null}
              healthCareServices={healthCareServices}
              updateServiceRequest={updateServiceRequest}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <ComplementaryInfo
              title="Referral Complementary Info"
              workflowInstance={workflowInstance}
              dataSchema={getParseColumn(workflowInstance, 'dataSchema')}
              data={getParseColumn(workflowInstance, 'data')}
              refreshWorkflow={setExternalRefreshWorkFlow}
              openCollapseExternal={openCollapse}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            {patient && (
              <PatientSummary
                title="Patient Information"
                patient={patient}
                openCollapseExternal={openCollapse}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <ReferralIdentifiers
              serviceRequest={serviceRequest}
              isOpen={openCollapse}
              onUpdateIdentifiers={onUpdateIdentifiers}
            />
          </Grid>

          <Grid item xs={12}>
            <ReferralHistory
              serviceRequest={serviceRequest}
              openCollapseExternal={openCollapse}
              patient={patient}
            />
          </Grid>

          <Grid item xs={12}>
            <TableCustom
              title={'Immunizations'}
              data={mapImmunizationsToDisplay(immunizations)}
              tableHead={TABLE_HEAD_IMMUNIZATIONS}
              handleOpen={handleOpenAddImmnization}
              titleButton={'Add Immunization'}
              statusList={STATUS_OPTION_IMMUNIZATION}
              openCollapseExternal={openCollapse}
            />
          </Grid>

          {/* <Grid item xs={12}>
            <Card>
              <Typography variant="subtitle1" sx={{ m: 2 }}>
                Cases
              </Typography>
              <SummaryTable header={TABLE_HEAD_CASES} data={dataCase} />
            </Card>
          </Grid> */}

          <Grid item xs={12}>
            <TableCustom
              title={'Contact Attempts'}
              data={Array.isArray(contactAttempt) && mapContactAttemptsReferral(contactAttempt)}
              tableHead={TABLE_HEAD_CONTACT}
              childrenButtons={
                <Grid item xs={4} display={'flex'} justifyContent={'flex-end'}>
                  <Button onClick={() => setOpenContactClientForm(true)}>Contact Client</Button>
                  <Button onClick={() => setOpenContactPCPForm(true)}>Contact PCP</Button>
                </Grid>
              }
              openCollapseExternal={openCollapse}
            />
          </Grid>

          <Grid item xs={12}>
            <TableCustom
              title={'Assessments / Forms'}
              data={Array.isArray(assessmentsForms) && assessmentsForms}
              tableHead={TABLE_HEAD_ASSESSMENTS}
              openCollapseExternal={openCollapse}
              childrenButtons={
                <Grid item xs={4} display={'flex'} justifyContent={'flex-end'}>
                  {checkAclValidation({
                    user,
                    acl: getRelatedAcls(CRS_ACLS.PATIENT.ASSESSMENTS.START),
                  }) && (
                    <StartAssessmentsButton
                      keyRegistry="crs-patient-assessments"
                      patientId={patient?.id ?? ''}
                      additionalQueryParams={`&referralUUID=${referralId}`}
                    />
                  )}
                </Grid>
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TableCustom
              title={'Consent Form'}
              data={mapCommunicationsToConsentFormDisplay(communications, 'consent_form')}
              tableHead={TABLE_HEAD_CONSENT_FORM}
              openCollapseExternal={openCollapse}
              statusList={STATUS_OPTION_IMMUNIZATION}
            />
          </Grid>

          <Grid item xs={12}>
            <TableCustom
              title={'Notes'}
              data={mapCommunicationsToNotesDisplay(communications, [
                'notes_referral',
                'mint_referral_notes',
              ])}
              tableHead={TABLE_HEAD_NOTES}
              handleOpen={handleOpenNote}
              titleButton={'Add A Note'}
              openCollapseExternal={openCollapse}
            />
          </Grid>

          <Grid item xs={12}>
            <DocumentList
              patient={patient}
              documentReferences={
                isDocumentsLoading || !(documentReferencesQuery as any)?.related
                  ? []
                  : documentReferences
              }
              resource={serviceRequest}
              showCollapse={true}
              refreshDocumentReference={refreshDocumentReference}
              openCollapseExternal={openCollapse}
              handleUpdateResource={handleUpdateReferral}
            />
          </Grid>
        </Grid>

        <FixAddressDialog
          isOpen={fixAddressModalOpen}
          handleClose={() => setFixAddressModalOpen(false)}
          handleSave={handleFixAddress}
          saveSelectedAddress={saveSelectedAddress}
          addressesList={patient?.address}
          addAddressTab={addAddressTabOpen}
          handleAddAddressOpen={setAddAddressTabOpen}
        />

        <AddressInCountyDialog
          isOpen={addressInCountyModalOpen}
          handleClose={() => setAddressInCountyModalOpen(false)}
          handleSave={handleConfirmAddressInCounty}
          addressSaved={workflowInstance?.data}
          saveCountyAddress={saveCountyAddress}
          addressList={patient?.address}
          handleOpenFromIsCounty={handleOpenFromIsCounty}
        />

        <ImmunizationAddForm
          open={openImmunization}
          onCancel={handleCloseAddImmunization}
          handleCreateImmunization={handleCreateImmunization}
          patient={patient}
        />

        <NoteAddForm
          open={openNote}
          patient={patient}
          onCancel={handleCloseAddNote}
          resource={[serviceRequest]}
          handleCreate={handleCreateCommunicationFhir}
          typeNote="notes_referral"
        />

        <InsuranceInfo
          patient={patient}
          serviceRequest={serviceRequest}
          open={openInsuranceInfo}
          onCancel={handleCloseInsuranceInfo}
          workflowReferral={true}
          handleInsuranceInfo={handleMarkItemCompleted}
          handleUpdateResource={handleUpdateReferral}
        />

        <MissingNotes
          option="Link Progress Notes"
          patient={patient}
          serviceRequest={serviceRequest}
          open={openMissingProgressNotes}
          onCancel={handleCloseMissingProgressNotes}
          workflowReferral={true}
          handleNotes={handleMarkItemCompleted}
          handleUpdateResource={handleUpdateReferral}
        />

        <MissingNotes
          option="Link Lab Notes"
          patient={patient}
          serviceRequest={serviceRequest}
          open={openMissingLabNotes}
          onCancel={handleCloseMissingLabNotes}
          workflowReferral={true}
          handleNotes={handleMarkItemCompleted}
          handleUpdateResource={handleUpdateReferral}
        />

        <ConsentForm
          patient={patient}
          communication={communications}
          serviceRequest={serviceRequest}
          handleCreateCommunication={handleCreateCommunication}
          open={openConsentForm}
          onCancel={handleCloseConsentForm}
        />

        <ContactForm
          option="Contact Client"
          lastRecord={lastContactClient}
          countRecord={countContactClient}
          referral={referralRecord}
          open={openContactClientForm}
          onCancel={handleCloseContactClientForm}
          handleCreateContactForm={handleCreateContactForm}
          assessmentsLinks={assessmentsLinks}
          patientId={patientId}
        />

        <ContactForm
          option="Document PCP"
          lastRecord={lastContactPCP}
          countRecord={countContactPCP}
          referral={referralRecord}
          open={openContactPCPForm}
          onCancel={handleCloseContactPCPForm}
          handleCreateContactForm={handleCreateContactForm}
        />
      </Container>
    </Page>
  );
});

export default ReferralDetails;
